import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { map, of } from 'rxjs';

import { environment } from '@kr/environment';
import { UserProfile } from '@kr/models';

import { EnvName } from '../../environments/models/env-name.enum';
import { AccountApiService } from '../pages/account/api';

export const envName = environment.envName !== EnvName.Local ? environment.envName : EnvName.DEV;

type Permission = { data: { permissions: { only: string | string[]; redirectTo: string } } };

export const PERMISSIONS = new Map([
  [`cryptobanko.${envName}.limits-manager.manual`, ['limiterLimits']],
  [`cryptobanko.${envName}.treasurers.manual`, ['treasurerWallets', 'treasurerDashboard']],
]);

export const getPermissionsArray = (groups: string[]) => {
  const permissionsByGroups = groups.map(permission => PERMISSIONS.get(permission));
  return permissionsByGroups.flat();
};

export const containsSubstring = (arr1: string[], arr2: string[]) => {
  return arr1.some(str1 => arr2.some(str2 => str1.includes(str2)));
};

const redirectTo = (routePermission: Permission, isAllowed: boolean, router: Router) => {
  if (isAllowed) {
    return;
  }

  router.navigate([routePermission.data.permissions.redirectTo]);
};

export const check = (account: UserProfile, routePermission: Permission, router: Router): boolean => {
  const permissions = getPermissionsArray(account.groups);
  const routePermissions = routePermission.data.permissions.only;

  if (typeof routePermissions === 'string') {
    const isAllowed = permissions.includes(routePermissions);
    redirectTo(routePermission, isAllowed, router);
    return isAllowed;
  }

  if (routePermissions instanceof Array) {
    const isAllowed = containsSubstring(permissions, routePermissions);
    redirectTo(routePermission, isAllowed, router);
    return isAllowed;
  }

  return true;
};

let cache: UserProfile;

export const appPermissionsGuard = (routePermission: Permission) => {
  const accountApiService = inject(AccountApiService);
  const router: Router = inject(Router);
  if (!hasPermissionsCache()) {
    return accountApiService.getAccount().pipe(
      map((account: UserProfile) => {
        cache = account;
        return check(account, routePermission, router);
      }),
    );
  }

  if (hasPermissionsCache()) {
    return of(check(cache, routePermission, router));
  }

  return of(true);
};

export const setPermissionsCache = (value: UserProfile): void => {
  cache = value;
};

export const clearPermissionsCache = (): void => (cache = null);

export const hasPermissionsCache = (): boolean => !!cache;
